import React, { useCallback } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { WatchTriangleIcon } from '@/atoms/Icons/WatchTriangleIcon'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsLabel, ParagraphLG, TitleXS } from '@/atoms/Text'
import { BoundedImage } from '@/molecules/BoundedImage'
import { useSafeTrack } from '@/utils/analytics'

interface CloudinaryImagePaths {
  mobile: string
  tablet: string
  desktop: string
}
export interface ShowHeroProps {
  cloudinaryImgPaths: CloudinaryImagePaths
  imageClassName?: string
  showTitle: string
  showDescription: string
  ctaPath: string
  ctaText: string
  ctaClassName?: string
  imageContainerClassName?: string
  logoImgPath: string
}

export const ShowHero: React.FC<ShowHeroProps> = ({
  cloudinaryImgPaths,
  imageClassName,
  showTitle,
  showDescription,
  ctaPath,
  ctaText,
  logoImgPath,
}) => {
  const track = useSafeTrack()

  const handleFeaturedShowClick = useCallback(() => {
    track('Featured Show Watch Now Clicked', { showTitle, ctaPath })
  }, [ctaPath, showTitle, track])

  const words = (
    <div className="w-full">
      <header>
        <BoundedImage
          className="mb-2 flex items-center justify-center lg:justify-start"
          alt={showTitle}
          path={logoImgPath}
          width={386}
          height={104}
          sizes="(max-width: 640px) 358px, (max-width: 1024px) 386px, 386px"
          imageClassName="h-auto max-h-[40px] w-auto max-w-full object-contain md:max-h-[80px] lg:max-h-[104px] lg:max-w-[386px]"
        />
      </header>
      <ParagraphLG color="core-gray-300" className="mb-10 text-center lg:max-w-[50%] lg:text-left">
        {showDescription}
      </ParagraphLG>
      <div className="flex w-full justify-center lg:justify-start">
        <LinkButton
          data-testid="watch-now-button"
          className="flex max-w-[220px] items-center justify-center space-x-3.5 px-12 py-4"
          href={ctaPath}
          onClick={handleFeaturedShowClick}
          variant="white"
        >
          <TitleXS weight="bold" as={AsLabel} className="cursor-pointer whitespace-nowrap" color="black">
            {ctaText}
          </TitleXS>
          <WatchTriangleIcon />
        </LinkButton>
      </div>
    </div>
  )

  return (
    <>
      <div className={classNames('relative w-full mb-6 lg:mb-0 !min-h-[320px] lg:!min-h-[600px]')}>
        <ResponsiveBackgroundImage
          src={cloudinaryImgPaths.desktop}
          srcUrls={{
            xs: cloudinaryImgPaths.mobile,
            sm: cloudinaryImgPaths.mobile,
            md: cloudinaryImgPaths.tablet,
            lg: cloudinaryImgPaths.desktop,
            xl: cloudinaryImgPaths.desktop,
            '2xl': cloudinaryImgPaths.desktop,
          }}
          className={classNames(imageClassName)}
        />
        <div className="absolute bottom-0 h-full w-full rounded-lg bg-black-80deg-to-tr" />
        <div className="absolute bottom-0 h-full w-full rounded-lg bg-black-b-t" />
        <PaddedContainer className="hidden lg:absolute lg:bottom-12 lg:-ml-8 lg:block">{words}</PaddedContainer>
      </div>
      <PaddedContainer className="lg:hidden">{words}</PaddedContainer>
    </>
  )
}
